/**
 * Device Operations Table - Frontend
 * @description Unified table component for displaying user devices with operational information
 * Combines the best features from both original and modern implementations
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';

// Types
import {
  DeviceDashboardTableData,
  DashboardOperationTableActions,
} from '@/types/operational-ui.types';

// UI Components from the main system
import { TableHeader } from '@/components/shared/tables/TableHeader/TableHeader';
import { TableRow } from '@/components/shared/tables/TableRow/TableRow';
import { FilterDialog } from '@/components/shared/FilterDialog';

// Hooks from the main system
import { useTableFilters } from '@/hooks/useTableFilters';

// Configuration
import { getDeviceDashboardTableColumns } from '@/constants/tables/deviceDashboardTable.config';

// Table cells
import { DeviceDashboardActionsCell } from './deviceDashboardTableCells/DeviceDashboardActionsCell';
import { CellContext } from '@tanstack/react-table';

interface DeviceDashboardTableProps {
  devices: DeviceDashboardTableData[];
  loading?: boolean;
  onViewOperations: (deviceId: string) => void;
  onViewAllLectures: (deviceId: string) => void;
  onViewAllPendingLectures: (deviceId: string) => void;
  onRefresh?: () => void;
  onExport?: (format: 'csv' | 'excel') => void;
  actions?: DashboardOperationTableActions;
  userId?: string;
  userInfo?: { name?: string };
  showAdvancedExport?: boolean;
}

export const DeviceDashboardTable: React.FC<DeviceDashboardTableProps> = ({
  devices,
  loading = false,
  onViewOperations,
  onViewAllLectures,
  onViewAllPendingLectures,
}) => {
  const { t } = useTranslation();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'lastOperation', desc: true },
  ]);

  // Get column configuration
  const columnConfigs = useMemo(() => getDeviceDashboardTableColumns(t), [t]);

  const {
    filterState,
    columnFilters,
    openFilter,
    closeFilter,
    setSearch,
    toggleOption,
    clearFilter,
    applyFilter,
    getFilteredOptions,
  } = useTableFilters(devices, columnConfigs);

  const columnHelper = createColumnHelper<DeviceDashboardTableData>();

  // Build columns from config + actions
  const columns: ColumnDef<DeviceDashboardTableData>[] = useMemo(
    () => [
      ...columnConfigs.map((config) =>
        columnHelper.accessor(config.accessor, {
          id: config.id,
          header: ({ column }) => {
            const filterValue = column.getFilterValue() as string[] | undefined;
            const isFiltered = Array.isArray(filterValue) && filterValue.length > 0;
            return (
              <TableHeader
                header={column}
                columnConfig={config}
                onFilterClick={(columnId) => openFilter(columnId, columnFilters)}
                isFiltered={isFiltered}
              />
            );
          },
          cell:
            config.cell ||
            (({ getValue }) => {
              const value = getValue();
              return <span className="text-gray-900">{value?.toString() || '-'}</span>;
            }),
          enableSorting: config.enableSorting,
          enableColumnFilter: config.enableFilter,
          filterFn:
            config.filterType === 'select'
              ? (row, _columnId, filterValue) => {
                  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
                  const value = row.getValue(_columnId);
                  return filterValue.includes(value?.toString() || '');
                }
              : undefined,
        })
      ),
      // Actions column
      columnHelper.display({
        id: 'actions',
        header: t('pages.operations.devices.columns.actions'),
        cell: ({ row }) => {
          return (
            <DeviceDashboardActionsCell
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              info={{ row, getValue: () => null } as CellContext<DeviceDashboardTableData, any>}
              actions={{
                onViewOperations,
                onViewAllLectures,
                onViewAllPendingLectures,
                // onCenterMap: actions?.onCenterMap,
              }}
            />
          );
        },
      }),
    ],
    [
      columnConfigs,
      columnHelper,
      t,
      openFilter,
      columnFilters,
      onViewOperations,
      onViewAllLectures,
      onViewAllPendingLectures,
      // actions?.onCenterMap,
    ]
  );

  const table = useReactTable({
    data: devices,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleRowClick = (deviceId: string | undefined) => {
    if (!deviceId) return;
    setSelectedRowId((current) => (current === deviceId ? null : deviceId));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg text-gray-600">
            {t('pages.operations.devices.loading')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Filter Dialogs */}
      {columnConfigs
        .filter((config) => config.enableFilter)
        .map((config) => {
          const filterTitleKey = `pages.operations.devices.filters.${config.id}`;
          return (
            <FilterDialog
              key={config.id}
              isOpen={!!filterState[config.id]?.isOpen}
              onClose={() => closeFilter(config.id)}
              title={t(filterTitleKey)}
              searchValue={filterState[config.id]?.search || ''}
              onSearchChange={(value) => setSearch(config.id, value)}
              options={getFilteredOptions(config.id)}
              selectedOptions={filterState[config.id]?.selected || []}
              onOptionToggle={(option) => toggleOption(config.id, option)}
              onClear={() => clearFilter(config.id)}
              onApply={() => applyFilter(config.id)}
              getOptionLabel={config.getOptionLabel}
              textCase={config.textCase}
            />
          );
        })}

      {/* Table Container with scroll */}
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
          <table className="w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-brand-gray-dark">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-brand-white tracking-wider sentence-case"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  row={row}
                  index={index}
                  selectedRowId={selectedRowId}
                  onRowClick={handleRowClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty state */}
      {devices.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">
            {t('pages.operations.devices.empty.title')}
          </div>
          <p className="text-gray-500 text-sm">{t('pages.operations.devices.empty.description')}</p>
        </div>
      )}
    </div>
  );
};
